import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BarChart3, Users, Clock3, CheckCircle2, FileDown, Flag,
  UserCheck, UserX, CalendarPlus,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ImpactReportModal from "@/components/ImpactReportModal";
import MetricsCharts from "@/components/organizer/MetricsCharts";
import ReportsTab from "@/components/organizer/ReportsTab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AttendanceTab from "@/components/AttendanceTab";
import { api, CATEGORIES, CITIES, FORMATS } from "@/lib/api";

export default function OrganizerPage() {
  const [params] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [reports, setReports] = useState([]);
  const [exportOpen, setExportOpen] = useState(params.get("report") === "1");

  useEffect(() => {
    if (params.get("report") === "1") setExportOpen(true);
  }, [params]);
  const [newListing, setNewListing] = useState({ title: "", category: "", capacity: "", description: "", city: "Jakarta", format: "Offline", date: "" });
  const [posting, setPosting] = useState(false);

  const loadReports = useCallback(() => api.get("/reports").then((r) => setReports(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/impact-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/applications").then((r) => setApplications(r.data)).catch(() => {});
    loadReports();
  }, [loadReports]);

  const updateApp = async (id, status) => {
    await api.patch(`/applications/${id}`, { status });
    setApplications((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    toast.success(`Aplikasi relawan ${status.toLowerCase()}.`);
  };

  const updateReport = async (id, status) => {
    await api.patch(`/reports/${id}`, { status });
    loadReports();
    toast.success(`Status laporan diubah: ${status}`);
  };

  const postListing = async () => {
    if (!newListing.title || !newListing.category || !newListing.capacity) {
      toast.error("Lengkapi judul, kategori, dan kapasitas.");
      return;
    }
    setPosting(true);
    try {
      await api.post("/events", { ...newListing, capacity: parseInt(newListing.capacity, 10) });
      toast.success(`Inisiatif "${newListing.title}" berhasil diterbitkan dan langsung tampil di Jelajahi Aksi!`);
      setNewListing({ title: "", category: "", capacity: "", description: "", city: "Jakarta", format: "Offline", date: "" });
    } catch {
      toast.error("Gagal menerbitkan inisiatif.");
    } finally {
      setPosting(false);
    }
  };

  if (!stats) return <div className="py-32 text-center text-slate-400 font-semibold">Memuat dashboard...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight">Organizer Hub</h1>
          <p className="text-slate-500 mt-2">Kelola inisiatif, relawan, laporan warga, dan dampak organisasimu.</p>
        </div>
        <Button data-testid="export-report-btn" onClick={() => setExportOpen(true)} className="rounded-full bg-slate-900 hover:bg-slate-700 text-white font-bold">
          <FileDown className="w-4 h-4 mr-2" /> Ekspor Laporan Dampak
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {[
          { icon: Users, label: "Total Relawan Dimobilisasi", val: stats.total_volunteers.toLocaleString("id-ID"), testid: "stat-volunteers" },
          { icon: Clock3, label: "Jam Relawan Terkontribusi", val: stats.total_hours.toLocaleString("id-ID"), testid: "stat-hours" },
          { icon: CheckCircle2, label: "Proyek Selesai Terverifikasi", val: stats.completed_projects, testid: "stat-projects" },
          { icon: BarChart3, label: "Kegiatan Aktif", val: stats.active_events, testid: "stat-active" },
        ].map((s) => (
          <div key={s.label} data-testid={s.testid} className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-5">
            <span className="inline-flex bg-emerald-50 text-emerald-600 rounded-xl p-2 mb-3"><s.icon className="w-5 h-5" /></span>
            <p className="font-heading font-black text-3xl text-slate-900">{s.val}</p>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="metrics" className="mt-10">
        <TabsList className="rounded-full bg-slate-100 p-1 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-metrics" value="metrics" className="rounded-full font-bold data-[state=active]:bg-white">Metrik Dampak</TabsTrigger>
          <TabsTrigger data-testid="tab-applications" value="applications" className="rounded-full font-bold data-[state=active]:bg-white">Aplikasi Relawan</TabsTrigger>
          <TabsTrigger data-testid="tab-listing" value="listing" className="rounded-full font-bold data-[state=active]:bg-white">Buat Listing Baru</TabsTrigger>
          <TabsTrigger data-testid="tab-attendance" value="attendance" className="rounded-full font-bold data-[state=active]:bg-white">Daftar Hadir</TabsTrigger>
          <TabsTrigger data-testid="tab-reports" value="reports" className="rounded-full font-bold data-[state=active]:bg-white">
            <Flag className="w-4 h-4 mr-1.5 text-rose-600" /> Laporan Warga
            <span className="ml-1.5 bg-rose-100 text-rose-700 text-xs font-black rounded-full px-2">{reports.filter((r) => r.status === "Baru Masuk").length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-6">
          <MetricsCharts stats={stats} />
        </TabsContent>

        <TabsContent value="applications" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {applications.map((a) => (
              <div key={a.id} data-testid={`application-row-${a.id}`} className="flex flex-wrap items-center gap-3 justify-between px-6 py-4 border-b border-slate-100 last:border-0">
                <div>
                  <p className="font-bold text-slate-900">{a.volunteer_name}</p>
                  <p className="text-xs text-slate-500">{a.event_title} · Daftar {a.applied_at}</p>
                  <div className="flex gap-1.5 mt-1">
                    {a.skills.map((s) => <span key={s} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">{s}</span>)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${a.status === "Diterima" ? "bg-emerald-100 text-emerald-800" : a.status === "Ditolak" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
                  {a.status === "Menunggu" && (
                    <>
                      <Button data-testid={`approve-app-${a.id}`} size="sm" onClick={() => updateApp(a.id, "Diterima")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
                        <UserCheck className="w-4 h-4 mr-1" /> Terima
                      </Button>
                      <Button data-testid={`reject-app-${a.id}`} size="sm" variant="outline" onClick={() => updateApp(a.id, "Ditolak")} className="rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50">
                        <UserX className="w-4 h-4 mr-1" /> Tolak
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="listing" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-7 max-w-2xl">
            <h3 className="font-heading font-extrabold text-slate-900 mb-5 flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-emerald-600" /> Posting Kebutuhan Relawan Baru</h3>
            <div className="space-y-4">
              <Input data-testid="listing-title-input" placeholder="Judul kegiatan (mis. Aksi Tanam 1000 Mangrove)" value={newListing.title} onChange={(e) => setNewListing({ ...newListing, title: e.target.value })} className="rounded-xl" />
              <div className="grid grid-cols-2 gap-4">
                <Select value={newListing.category} onValueChange={(v) => setNewListing({ ...newListing, category: v })}>
                  <SelectTrigger data-testid="listing-category-select" className="rounded-xl" aria-label="Kategori inisiatif"><SelectValue placeholder="Kategori" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="listing-capacity-input" type="number" min="1" placeholder="Batas kapasitas relawan" aria-label="Batas kapasitas relawan" value={newListing.capacity} onChange={(e) => setNewListing({ ...newListing, capacity: e.target.value })} className="rounded-xl" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Select value={newListing.city} onValueChange={(v) => setNewListing({ ...newListing, city: v })}>
                  <SelectTrigger data-testid="listing-city-select" className="rounded-xl" aria-label="Kota"><SelectValue placeholder="Kota" /></SelectTrigger>
                  <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={newListing.format} onValueChange={(v) => setNewListing({ ...newListing, format: v })}>
                  <SelectTrigger data-testid="listing-format-select" className="rounded-xl" aria-label="Format kegiatan"><SelectValue placeholder="Format" /></SelectTrigger>
                  <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="listing-date-input" type="date" aria-label="Tanggal kegiatan" value={newListing.date} onChange={(e) => setNewListing({ ...newListing, date: e.target.value })} className="rounded-xl" />
              </div>
              <Textarea data-testid="listing-description-input" placeholder="Deskripsi kegiatan, kebutuhan keahlian, dan detail lokasi..." value={newListing.description} onChange={(e) => setNewListing({ ...newListing, description: e.target.value })} className="rounded-xl min-h-[100px]" />
              <Button data-testid="listing-submit-btn" onClick={postListing} disabled={posting} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8">{posting ? "Menerbitkan..." : "Terbitkan Inisiatif"}</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="mt-6">
          <AttendanceTab />
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <ReportsTab reports={reports} onUpdate={updateReport} />
        </TabsContent>
      </Tabs>

      <ImpactReportModal open={exportOpen} onOpenChange={setExportOpen} stats={stats} />
    </div>
  );
}
